import React, {useCallback} from 'react';

import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {OnePaneLayoutFullWidthSection} from '@udemy/react-layout-components';
import {
    PersonalPlanBanner as AIEraCareerBannerComponent,
    PersonalPlanBannerProps,
    type PersonalPlanBannerContent,
    SubscriptionOfferPresented,
    SubscriptionLearnMoreEvent,
} from '@udemy/subscription-browse';
import {
    AnnualSubscriptionPlanPricingOption,
    ConsumerSubscriptionPlan,
    ConsumerSubscriptionValuePlan,
    SubscriptionPlan,
    SubscriptionPlanContext,
    SubscriptionPlanProductType,
    useConsumerSubscriptionPlan,
    useConsumerSubscriptionValuePlan,
} from '@udemy/subscription-graphql';

import {PERSONAL_PLAN_BANNER_VALUE_PROP_REVAMP} from 'src/lib/experimentation-helper';

import bannerContentData from './data/ai-career-banner-data.json';

type AICareerBannerVariant = 'western' | 'indian' | 'japanese' | 'default';
type BannerContentKey = AICareerBannerVariant | 'value';

// Shape of the experiment's `configuration` payload. Each flag, when true,
// selects a regional variant of the Personal Plan banner.
interface PersonalPlanBannerExperimentConfig {
    western_version?: boolean;
    indian_version?: boolean;
    japan_version?: boolean;
}

// Compile-time cast only; runtime values are untouched (same pattern as value-prop-module).
const BANNER_CONTENT = bannerContentData as Record<BannerContentKey, PersonalPlanBannerContent>;

type ValidPlan = (ConsumerSubscriptionPlan | ConsumerSubscriptionValuePlan) & {
    annualPlanOption: AnnualSubscriptionPlanPricingOption;
};

const hasValidPricing = (
    context: SubscriptionPlanContext<
        ConsumerSubscriptionPlan | ConsumerSubscriptionValuePlan
    > | null,
): context is ValidPlan => {
    return Boolean(
        context &&
            !context.isLoading &&
            'annualPlanOption' in context &&
            context.annualPlanOption?.monthlyPrice?.amount,
    );
};

const AIEraCareerBannerWrapper = ({children}: {children: React.ReactNode}) => (
    <OnePaneLayoutFullWidthSection>
        <OnePaneLayoutFullWidthSection.Content>{children}</OnePaneLayoutFullWidthSection.Content>
    </OnePaneLayoutFullWidthSection>
);

type AIEraCareerBannerFeatureProps = Omit<PersonalPlanBannerProps, 'ctaSlot'> & {
    subscriptionPlan: SubscriptionPlan;
    annualPlanOption: AnnualSubscriptionPlanPricingOption;
};

/**
 * AI Era Career Banner Implementation
 */
const AIEraCareerBannerFeature = ({
    subscriptionPlan,
    annualPlanOption,
    ...otherProps
}: AIEraCareerBannerFeatureProps) => {
    const subscriptionPrice = parseFloat(annualPlanOption.monthlyPrice.amount);

    const onClick = useCallback(() => {
        Tracker.publishEvent(
            new SubscriptionLearnMoreEvent(
                annualPlanOption.id,
                SubscriptionPlanProductType.Consumersubscription,
                'reimagine_career',
                [annualPlanOption.id],
                null,
            ),
        );
    }, [annualPlanOption]);

    const trackImpression = () => {
        Tracker.publishEvent(
            new SubscriptionOfferPresented({
                subscriptionPlan,
                productType: SubscriptionPlanProductType.Consumersubscription,
                countryCode: annualPlanOption.monthlyPrice.currency ?? 'USD',
                uiRegion: 'reimagine_career',
            }),
        );
    };

    return (
        <TrackImpression trackFunc={trackImpression}>
            <div>
                <AIEraCareerBannerComponent
                    subscriptionPrice={subscriptionPrice}
                    onCtaClick={onClick}
                    {...otherProps}
                />
            </div>
        </TrackImpression>
    );
};

/**
 * AIEraCareerBanner
 *
 * Logged-out homepage subscription banner.
 * - Value Plan visitors: dedicated Value Plan copy + `/pricing/` CTA.
 * - Everyone else: Personal Plan banner whose variant (western / indian /
 *   japanese / default) is chosen by the `pp_module_value_prop_revamp` experiment.
 */
export const AIEraCareerBanner = () => {
    const valuePlanContext = useConsumerSubscriptionValuePlan();
    const personalPlanContext = useConsumerSubscriptionPlan();
    const {featureVariantAssignments, isLoading: areFeaturesLoading} = useFeatures([
        PERSONAL_PLAN_BANNER_VALUE_PROP_REVAMP,
    ]);

    if (valuePlanContext?.isLoading || personalPlanContext?.isLoading || areFeaturesLoading) {
        return (
            <AIEraCareerBannerWrapper>
                <AIEraCareerBannerComponent
                    isLoading={true}
                    bannerContent={BANNER_CONTENT.default}
                />
            </AIEraCareerBannerWrapper>
        );
    }

    // Resolve the banner variant from the experiment configuration. Priority is
    // western -> indian -> japanese -> default (default when the experiment is
    // off/absent), mirroring the package's own internal precedence.
    const bannerConfig = featureVariantAssignments.get(PERSONAL_PLAN_BANNER_VALUE_PROP_REVAMP)
        ?.configuration as PersonalPlanBannerExperimentConfig | undefined;
    const selectedVariant: AICareerBannerVariant =
        bannerConfig?.western_version === true
            ? 'western'
            : bannerConfig?.indian_version === true
              ? 'indian'
              : bannerConfig?.japan_version === true
                ? 'japanese'
                : 'default';

    const hasValuePlan = hasValidPricing(valuePlanContext);
    const plan = hasValuePlan ? valuePlanContext : personalPlanContext;

    if (!hasValidPricing(plan)) {
        return null;
    }

    const bannerContent = hasValuePlan ? BANNER_CONTENT.value : BANNER_CONTENT[selectedVariant];

    return (
        <AIEraCareerBannerWrapper>
            <AIEraCareerBannerFeature
                subscriptionPlan={plan}
                annualPlanOption={plan.annualPlanOption}
                bannerContent={bannerContent}
            />
        </AIEraCareerBannerWrapper>
    );
};
